import React, { useEffect, useState, useRef } from "react";
import { MdMenu, MdClose, MdFacebook, MdExpandMore, MdExpandLess } from "react-icons/md";
import { FaSearch, FaShoppingCart } from "react-icons/fa";
import { BsTwitterX } from "react-icons/bs";
import { FaInstagram } from "react-icons/fa";

import { FaRegCircleUser, FaCircleUser } from "react-icons/fa6";
import logo from "../asset/maysam/maysam.jpg";
import SidebarMenu from "./Sidebar-Menu";
import CartComponent from "./Cart";
import SearchSidebar from "./SearchSidebar";
import { useNavigate } from "react-router-dom";
import { fetchCategories, getCartItemsCount } from "../services/api";
import { Category } from "../utiles/types";

const Navbar: React.FC = (): JSX.Element => {
  const [sidebar, setSidebar] = useState<boolean>(false);
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [searchSidebarOpen, setSearchSidebarOpen] = useState<boolean>(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState<boolean>(false);
  const [isCategoriesOpen, setIsCategoriesOpen] = useState<boolean>(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [cartItemsCount, setCartItemsCount] = useState<number>(0);

  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const toggleSidebar = (): void => {
    setSidebar(!sidebar);
  };

  const toggleCartSidebar = (): void => {
    setIsCartOpen(!isCartOpen);
  };

  const toggleSearchSidebar = (): void => {
    setSearchSidebarOpen(!searchSidebarOpen);
  };

  const toggleMobileDropdown = (): void => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  };

  const closeMobileDropdown = (): void => {
    setIsMobileDropdownOpen(false);
    setIsCategoriesOpen(false);
  };

  const toggleCategories = (): void => {
    setIsCategoriesOpen(!isCategoriesOpen);
  };

  const checkUserInfo = (): boolean => {
    const userInfo = localStorage.getItem("user");
    return userInfo !== null;
  };

  const handleUserProfileNavigate = () => {
    if (checkUserInfo()) {
      navigate("/user-profile");
    } else {
      navigate("/auth/login");
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const fetchedCategories = await fetchCategories();
        setCategories(fetchedCategories);
        const cartCount = await getCartItemsCount();
        setCartItemsCount(cartCount);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    const intervalId = setInterval(() => {
      fetchData();
    }, 6000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target as Node)) {
        closeMobileDropdown();
      }
    };

    if (isMobileDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isMobileDropdownOpen]);

  return (
    <>
      <nav className="navbar sticky top-0 left-0 w-full flex items-center justify-between lg:pl-4 lg:pr-4 bg-white z-50">
        <div className="hidden md:flex h-22 flex-col w-full">
          {/* For Large Screens */}
          <div className="hidden h-20 md:flex items-center justify-between w-full pl-16 pr-16 text-black border-gray-200">
            <div className="flex items-center space-x-4">
              <MdMenu className="text-2xl cursor-pointer" onClick={toggleSidebar} />
              

              {/* Collection button with hover mega-menu moved into top navbar */}
              <div className="group relative">
                <button
                  onClick={() => handleNavigate("/collection")}
                  className="relative text-md font-semibold px-4 py-2 after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-0.5 after:bg-black after:transform after:scale-x-0 after:origin-right hover:after:scale-x-100 hover:after:origin-left after:transition-transform after:duration-300"
                >
                  COLLECTION
                </button>

                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute left-0 top-full w-[500px] bg-white text-black border-t border-gray-200 z-50">
                  <div className="max-w-7xl mx-auto grid grid-cols-12 gap-6 px-8 py-12">
                    <div className="col-span-5">
                      <h3 className="text-sm tracking-widest mb-6">COLLECTIONS</h3>
                      <div className="grid grid-cols-2 gap-x-10 gap-y-3">
                        {categories.map((category, idx) => (
                          <button
                            key={category.id}
                            onClick={() => handleNavigate(`/collection/${category.name.toLowerCase().replace(/ /g, "-")}`)}
                            className="text-sm text-left font-light hover:underline"
                          >
                            {category.name.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>

                    
                  </div>
                </div>
              </div>
            </div>

            {/* Absolute positioning for logo */}
            <img
              src={logo}
              alt="BlackWilbur"
              className="absolute left-1/2 transform -translate-x-1/2 h-20 w-20 text-black cursor-pointer"
              onClick={() => handleNavigate("/")}
            />

            <div className="flex items-center space-x-4">
              <FaRegCircleUser
                onClick={handleUserProfileNavigate} // Update the onClick handler
                className="text-2xl cursor-pointer"
              />
              <button onClick={toggleSearchSidebar} className="text-xl">
                <FaSearch />
              </button>

              <div className="relative">
                <FaShoppingCart onClick={toggleCartSidebar} className="text-xl cursor-pointer" />
                <span className="absolute -top-2 -right-2 bg-red-600 text-black rounded-full text-xs px-1.5 py-0.5">
                  {cartItemsCount || 0}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* New Mobile Navbar */}
        <div className="flex md:hidden items-center justify-between w-full h-16 px-4 bg-white shadow-md relative">
          {/* Left Logo */}
          <img
            src={logo}
            alt="Maysam"
            className="h-8 cursor-pointer"
            onClick={() => handleNavigate("/")}
          />
        
      
          {/* Right Hamburger */}
          <MdMenu className="text-2xl cursor-pointer" onClick={toggleMobileDropdown} />

          {/* Mobile Dropdown Menu */}
          {isMobileDropdownOpen && (
            <div
              ref={mobileDropdownRef}
              className="absolute top-full left-0 w-full bg-white text-black border-t border-gray-200 z-50 shadow-lg max-h-screen overflow-y-auto"
            >
              <div className="flex flex-col">
                {/* Close Button */}
                <div className="flex justify-end p-4">
                  <MdClose className="text-2xl cursor-pointer" onClick={closeMobileDropdown} />
                </div>

                {/* Menu Items */}
                <button
                  onClick={() => {
                    handleNavigate("/about-us/contact");
                    closeMobileDropdown();
                  }}
                  className="px-6 py-4 text-left hover:bg-gray-100 transition-colors border-b border-gray-100"
                >
                  CONTACT
                </button>

                {/* Collection with Sub-dropdown */}
                <div className="border-b border-gray-100">
                  <button
                    onClick={toggleCategories}
                    className="w-full px-6 py-4 text-left hover:bg-gray-100 transition-colors flex items-center justify-between"
                  >
                    COLLECTION
                    {isCategoriesOpen ? <MdExpandLess /> : <MdExpandMore />}
                  </button>
                  {isCategoriesOpen && (
                    <div className="bg-gray-50">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => {
                            handleNavigate(`/collection/${category.name.toLowerCase().replace(/ /g, "-")}`);
                            closeMobileDropdown();
                          }}
                          className="w-full px-8 py-3 text-left hover:bg-gray-200 transition-colors text-sm"
                        >
                          {category.name.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    handleNavigate("/about-us");
                    closeMobileDropdown();
                  }}
                  className="px-6 py-4 text-left hover:bg-gray-100 transition-colors border-b border-gray-100"
                >
                  ABOUT US
                </button>

                <button
                  onClick={() => {
                    handleNavigate("/auth/login");
                    closeMobileDropdown();
                  }}
                  className="px-6 py-4 text-left hover:bg-gray-100 transition-colors border-b border-gray-100"
                >
                  LOGIN
                </button>

                {/* Social Links */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="text-sm mb-3">FOLLOW US:</div>
                  <div className="flex space-x-4">
                    <MdFacebook className="text-xl cursor-pointer hover:text-blue-600" />
                    <BsTwitterX className="text-xl cursor-pointer hover:text-black" />
                    <FaInstagram className="text-xl cursor-pointer hover:text-pink-600" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/*Search Sidebar component */}
      <SearchSidebar isOpen={searchSidebarOpen} onClose={toggleSearchSidebar} />

      {/* Sidebar component */}
      <SidebarMenu isOpen={sidebar} onClose={() => setSidebar(false)} />

      {/* Add to Cart Sidebar */}
      <div className="text-black">
        <CartComponent isOpen={isCartOpen} onClose={toggleCartSidebar} cartItems={[]} />
      </div>
    </>
  );
};

export default Navbar;